import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../api';
import { useNavigate } from 'react-router-dom';
import SecretariatAIAssistant from '../../components/SecretariatAIAssistant';

const SecretariatDashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        activeQuestions: 0,
        pendingAgendas: 0,
        upcomingMeetings: 0,
        systemHealth: 'LOADING'
    });

    const [tasks, setTasks] = useState([]);
    const [notifications, setNotifications] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem('token');
                const headers = { Authorization: `Bearer ${token}` };

                const statsRes = await axios.get(`${API_BASE_URL}/api/secretariat/dashboard/stats`, { headers }).catch(e => ({ data: { activeQuestions: 0, pendingAgendas: 0, upcomingMeetings: 0, systemHealth: 'ERR' } }));
                setStats(statsRes.data);

                const tasksRes = await axios.get(`${API_BASE_URL}/api/secretariat/tasks`, { headers }).catch(e => ({ data: [] }));
                setTasks(tasksRes.data);

                const notifRes = await axios.get(`${API_BASE_URL}/api/secretariat/notifications`, { headers }).catch(e => ({ data: [] }));
                setNotifications(notifRes.data);
            } catch (err) {
                console.error('Dashboard load failed', err);
            }
        };
        fetchData();
    }, []);

    const ICONS = {
        question: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>,
        agenda: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>,
        meeting: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>,
        task: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" ry="1" /></svg>,
        alert: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>,
        check: <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>,
        shield: <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
    };

    const Widget = ({ title, count, color, icon, detail, link }) => (
        <div
            onClick={() => link && navigate(link)}
            style={{
                backgroundColor: '#FFFFFF',
                padding: '30px',
                borderRadius: '24px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.03)',
                border: '1px solid #F0E8DB',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                cursor: link ? 'pointer' : 'default',
                display: 'flex',
                flexDirection: 'column'
            }}
            onMouseEnter={e => {
                if (link) {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 20px 40px rgba(44, 26, 16, 0.08)';
                    e.currentTarget.style.borderColor = color;
                }
            }}
            onMouseLeave={e => {
                if (link) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.03)';
                    e.currentTarget.style.borderColor = '#F0E8DB';
                }
            }}
        >
            <div style={{
                position: 'absolute',
                top: '-20px',
                right: '-20px',
                width: '100px',
                height: '100px',
                backgroundColor: `${color}08`,
                borderRadius: '50%'
            }}></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', position: 'relative' }}>
                <span style={{ color: '#999', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px' }}>{title}</span>
                <span style={{ color: color, display: 'flex' }}>{icon}</span>
            </div>
            <div style={{
                fontSize: '32px',
                fontWeight: '800',
                color: '#2C1A10',
                marginBottom: '8px',
                position: 'relative',
                fontFamily: "'Outfit', sans-serif",
                letterSpacing: '-0.5px'
            }}>
                {count}
            </div>
            <div style={{ fontSize: '14px', color: '#777', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px', position: 'relative' }}>
                <span style={{ width: '6px', height: '6px', backgroundColor: color, borderRadius: '50%' }}></span>
                {detail}
            </div>
        </div>
    );

    return (
        <div style={{ fontFamily: '"Outfit", sans-serif', maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
            <div style={{ marginBottom: '40px' }}>
                <h1 style={{ fontSize: '32px', fontWeight: '900', color: '#2C1A10', margin: 0 }}>Legislative Pulse</h1>
                <p style={{ color: '#666', marginTop: '5px', fontSize: '16px' }}>Real-time overview of secretariat operations and governance.</p>
            </div>

            {/* Top Widgets - 3 Column Layout */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: '30px',
                marginBottom: '40px'
            }}>
                <Widget title="Active Questions" count={stats.activeQuestions} color="#C0722C" link="/secretariat/questions" icon={ICONS.question} detail="Pending Secretariat Review" />
                <Widget title="Pending Agendas" count={stats.pendingAgendas} color="#2C1A10" link="/secretariat/agendas" icon={ICONS.agenda} detail="Awaiting Decision Protocol" />
                <Widget title="Upcoming Meetings" count={stats.upcomingMeetings} color="#2E7D32" link="/secretariat/meetings" icon={ICONS.meeting} detail="Scheduled Sessions (Next 7d)" />
            </div>

            {/* Sleek Security Banner - Full Width */}
            <div style={{
                width: '100%',
                boxSizing: 'border-box',
                marginBottom: '50px',
                background: 'linear-gradient(90deg, #2C1A10 0%, #4E342E 100%)',
                borderRadius: '24px',
                padding: '24px 40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: '0 15px 35px rgba(44, 26, 16, 0.15)',
                position: 'relative',
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.1)'
            }}>
                {/* Decorative Pattern Overlay */}
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0.1, pointerEvents: 'none' }}>
                    <svg width="100%" height="100%"><pattern id="banner-grid" width="30" height="30" patternUnits="userSpaceOnUse"><path d="M 30 0 L 0 0 0 30" fill="none" stroke="white" strokeWidth="0.5" /></pattern><rect width="100%" height="100%" fill="url(#banner-grid)" /></svg>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '25px', zIndex: 1 }}>
                    <div style={{
                        width: '50px',
                        height: '50px',
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        borderRadius: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: stats.systemHealth === 'HEALTHY' ? '#81C784' : '#E57373',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }}>
                        {ICONS.shield}
                    </div>
                    <div>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '4px' }}>Network Security Link</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ fontSize: '20px', fontWeight: '900', color: '#FDF8F3' }}>
                                {stats.systemHealth === 'LOADING' ? 'SECURE ENCRYPTION IN PROGRESS...' : (stats.systemHealth === 'ERR' ? 'SECURITY PERIMETER BREACHED' : `SYSTEMS ${stats.systemHealth}`)}
                            </div>
                            <div style={{
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: stats.systemHealth === 'HEALTHY' ? '#4CAF50' : '#F44336',
                                boxShadow: `0 0 15px ${stats.systemHealth === 'HEALTHY' ? '#4CAF50' : '#F44336'}`,
                                animation: 'pulse-glow 2s infinite'
                            }} />
                        </div>
                    </div>
                </div>

                <div style={{ zIndex: 1, textAlign: 'right', borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: '40px' }}>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: '700', marginBottom: '4px' }}>OPERATIONAL STATUS</div>
                    <div style={{ color: '#C0722C', fontSize: '14px', fontWeight: '800', letterSpacing: '1px' }}>CERTIFIED SECURE</div>
                </div>

                <style>{`
                    @keyframes pulse-glow {
                        0% { transform: scale(1); opacity: 1; filter: brightness(1); }
                        50% { transform: scale(1.3); opacity: 0.7; filter: brightness(1.5); }
                        100% { transform: scale(1); opacity: 1; filter: brightness(1); }
                    }
                `}</style>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '40px' }}>
                {/* My Priority Tasks - Masterpiece Redesign */}
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#2C1A10', margin: 0 }}>My Core Directives</h3>
                        <span style={{ fontSize: '13px', color: '#C0722C', fontWeight: '700', cursor: 'pointer' }}>View All Registry</span>
                    </div>
                    <div style={{ backgroundColor: '#fff', borderRadius: '28px', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.04)', border: '1px solid #F0F0F0', minHeight: '450px' }}>
                        {tasks.length === 0 ? (
                            <div style={{ height: '450px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px', textAlign: 'center' }}>
                                <div style={{ width: '100px', height: '100px', backgroundColor: '#FDF8F3', borderRadius: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C0722C', marginBottom: '25px' }}>
                                    {ICONS.check}
                                </div>
                                <h4 style={{ fontSize: '22px', fontWeight: '800', color: '#2C1A10', marginBottom: '10px' }}>Operational Clearance</h4>
                                <p style={{ color: '#888', maxWidth: '300px', lineHeight: '1.6' }}>All high-priority legislative tasks have been successfully processed and archived.</p>
                            </div>
                        ) : (
                            <div style={{ padding: '10px' }}>
                                {tasks.map((task, idx) => (
                                    <div key={task._id} style={{
                                        padding: '24px',
                                        borderRadius: '20px',
                                        backgroundColor: idx % 2 === 0 ? '#FDF8F3' : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '20px',
                                        transition: 'all 0.2s ease',
                                        cursor: 'pointer',
                                        marginBottom: '5px'
                                    }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F8F1E9'}>
                                        <div style={{
                                            width: '48px',
                                            height: '48px',
                                            borderRadius: '14px',
                                            backgroundColor: '#fff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#C0722C',
                                            boxShadow: '0 4px 10px rgba(0,0,0,0.05)'
                                        }}>
                                            {ICONS.task}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: '800', color: '#2C1A10', fontSize: '16px' }}>{task.title}</div>
                                            <div style={{ fontSize: '13px', color: '#888', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                <span>Protocol ID: {task._id.substring(0, 8).toUpperCase()}</span>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                                                    {new Date(task.dueDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                                </span>
                                            </div>
                                        </div>
                                        <div style={{
                                            padding: '8px 16px',
                                            borderRadius: '12px',
                                            fontSize: '11px',
                                            fontWeight: '800',
                                            backgroundColor: task.priority === 'HIGH' ? '#FF5252' : '#C0722C',
                                            color: '#fff',
                                            textTransform: 'uppercase',
                                            letterSpacing: '1px'
                                        }}>{task.priority}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Recent Alerts - Masterpiece Redesign */}
                <div>
                    <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#2C1A10', marginBottom: '20px' }}>Security Feed</h3>
                    <div style={{ backgroundColor: '#4E342E', borderRadius: '28px', padding: '30px', boxShadow: '0 20px 50px rgba(78, 52, 46, 0.15)', border: '1px solid #5D4037', minHeight: '450px', position: 'relative' }}>
                        {notifications.length === 0 ? (
                            <div style={{ height: '390px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                                <div style={{ width: '80px', height: '80px', backgroundColor: 'rgba(253, 248, 243, 0.05)', borderRadius: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FDF8F3', marginBottom: '20px' }}>
                                    {ICONS.shield}
                                </div>
                                <h4 style={{ fontSize: '18px', fontWeight: '800', color: '#FDF8F3', marginBottom: '8px' }}>Perimeter Secure</h4>
                                <p style={{ color: 'rgba(253, 248, 243, 0.6)', fontSize: '14px', maxWidth: '200px', lineHeight: '1.6' }}>No legislative anomalies detected in the current session.</p>
                            </div>
                        ) : (
                            <div style={{ overflowY: 'auto' }}>
                                {notifications.map((notif, idx) => (
                                    <div key={notif.id || idx} style={{
                                        display: 'flex',
                                        gap: '20px',
                                        marginBottom: '25px',
                                        position: 'relative'
                                    }}>
                                        {idx < notifications.length - 1 && <div style={{ position: 'absolute', left: '9px', top: '25px', bottom: '-20px', width: '1.5px', backgroundColor: 'rgba(253, 248, 243, 0.1)' }} />}
                                        <div style={{
                                            width: '18px',
                                            height: '18px',
                                            borderRadius: '50%',
                                            backgroundColor: notif.type === 'alert' ? '#FF5252' : (notif.type === 'warning' ? '#FFCA28' : '#C0722C'),
                                            border: '3px solid #4E342E',
                                            zIndex: 2,
                                            boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
                                        }} />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '14px', color: '#FDF8F3', fontWeight: '700', lineHeight: '1.5' }}>{notif.text}</div>
                                            <div style={{ fontSize: '11px', color: '#C0722C', fontWeight: '800', marginTop: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>{notif.time || 'JUST NOW'}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <button style={{
                            width: 'calc(100% - 60px)',
                            position: 'absolute',
                            bottom: '30px',
                            left: '30px',
                            padding: '16px',
                            backgroundColor: 'rgba(253, 248, 243, 0.05)',
                            border: '1.5px solid rgba(253, 248, 243, 0.1)',
                            borderRadius: '16px',
                            color: '#FDF8F3',
                            fontWeight: '800',
                            fontSize: '13px',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }} onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(253, 248, 243, 0.1)';
                            e.currentTarget.style.borderColor = 'rgba(253, 248, 243, 0.3)';
                        }} onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(253, 248, 243, 0.05)';
                            e.currentTarget.style.borderColor = 'rgba(253, 248, 243, 0.1)';
                        }}>
                            System Audit Log
                        </button>
                    </div>
                </div>
            </div>
            <SecretariatAIAssistant />
        </div>
    );
};

export default SecretariatDashboard;
